import React from "react";
import { createPortal } from "react-dom";

import ChatErrorBanner from "./ChatErrorBanner";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";
import { CompetencyEditPanel } from "./CompetencyEditPanel";
import { FormBuilderSuggestedActions } from "./FormBuilderSuggestedActions";
import { GoalCoachSuggestedActions } from "./GoalCoachSuggestedActions";
import { GoalEditPanel } from "./GoalEditPanel";

import { useAskStream } from "../hooks/useAskStream";
import { useGoalCoachEnabled } from "../hooks/useGoalCoachEnabled";
import { useGoalCreation } from "../hooks/useGoalCreation";

import { useAskPerformYardStore } from "../AskPerformYardStore";
import { GOAL_COACH_START_QUERY } from "../constants";

const ChatContent: React.FC = () => {
	const hasNoDocuments = useAskPerformYardStore((state) => state.hasNoDocuments);
	const askHrEnabled = useAskPerformYardStore((state) => state.cachedAskHrEnabled);
	const goalCreationEnabled = useAskPerformYardStore((state) => state.cachedGoalCreationEnabled);
	const formCreationEnabled = useAskPerformYardStore((state) => state.cachedFormCreationEnabled);
	// RBAC-gated in index.tsx alongside formCreationEnabled — cached, unlike the live goal-coach hook.
	const formBuildingCoachEnabled = useAskPerformYardStore((state) => state.cachedFormBuildingCoachEnabled);
	const isOnFormBuilderPage = useAskPerformYardStore((state) => state.isOnFormBuilderPage);
	const isOnGoalCreationPage = useAskPerformYardStore((state) => state.isOnGoalCreationPage);
	const conversationStarted = useAskPerformYardStore((state) => state.messages.some((m) => m.role === "user"));
	const goalCoachEnabled = useGoalCoachEnabled();

	// Owned here (not in FormBuilderSuggestedActions) so the unmount-cleanup
	// doesn't abort the fetch the click just kicked off.
	const { sendQuery } = useAskStream();

	// The popup CTA sets pendingGoalCoachStart; fire the evaluation here rather than in
	// the popup, whose unmount-on-open would abort the request. Deferred a tick so the
	// fetch lands on the stable mount — a synchronous send on StrictMode's throwaway
	// mount gets aborted by that mount's cleanup, leaving the chat stuck on "Thinking…".
	const pendingGoalCoachStart = useAskPerformYardStore((state) => state.pendingGoalCoachStart);
	const consumePendingGoalCoachStart = useAskPerformYardStore((state) => state.consumePendingGoalCoachStart);
	React.useEffect(() => {
		if (!pendingGoalCoachStart) return;
		const timer = setTimeout(() => {
			if (consumePendingGoalCoachStart()) {
				void sendQuery({ query: GOAL_COACH_START_QUERY, agent: "goal-coach" });
			}
		}, 0);
		return () => clearTimeout(timer);
	}, [pendingGoalCoachStart, consumePendingGoalCoachStart, sendQuery]);

	const showNoDocumentsError =
		askHrEnabled &&
		hasNoDocuments &&
		!goalCreationEnabled &&
		!formCreationEnabled &&
		!(isOnGoalCreationPage && goalCoachEnabled) &&
		!(isOnFormBuilderPage && formBuildingCoachEnabled);
	const showFormBuilderChip =
		isOnFormBuilderPage && formCreationEnabled && !conversationStarted && !showNoDocumentsError;
	const showFormBuildingCoachChip =
		isOnFormBuilderPage && formBuildingCoachEnabled && !conversationStarted && !showNoDocumentsError;
	const showGoalCoachChip = isOnGoalCreationPage && goalCoachEnabled && !conversationStarted && !showNoDocumentsError;

	return (
		<>
			<ChatHeader />
			<div className="flex flex-1 flex-col overflow-hidden">
				{showNoDocumentsError ? <ChatErrorBanner type="no-documents" /> : <ChatMessages />}
			</div>
			{(showFormBuilderChip || showFormBuildingCoachChip) && (
				<div className="px-4">
					<FormBuilderSuggestedActions
						showSuggestQuestions={showFormBuilderChip}
						showSuggestImprovements={showFormBuildingCoachChip}
						onSelectSuggestQuestions={(query) => void sendQuery({ query, agent: "form" })}
						onSelectSuggestImprovements={(query) => void sendQuery({ query, agent: "form-building-coach" })}
					/>
				</div>
			)}
			{showGoalCoachChip && (
				<div className="px-4 pt-2">
					<GoalCoachSuggestedActions onSelect={(query) => void sendQuery({ query, agent: "goal-coach" })} />
				</div>
			)}
			<ChatInput />
		</>
	);
};

export const ChatContainer: React.FC = () => {
	const isOpen = useAskPerformYardStore((state) => state.isOpen);
	const isExpanded = useAskPerformYardStore((state) => state.isExpanded);
	const close = useAskPerformYardStore((state) => state.close);
	const isGoalEditPanelOpen = useAskPerformYardStore((state) => state.isGoalEditPanelOpen);
	const closeGoalEditPanel = useAskPerformYardStore((state) => state.closeGoalEditPanel);
	const isCompetencyEditPanelOpen = useAskPerformYardStore((state) => state.isCompetencyEditPanelOpen);
	const closeCompetencyEditPanel = useAskPerformYardStore((state) => state.closeCompetencyEditPanel);
	const markGoalDeleted = useAskPerformYardStore((state) => state.markGoalDeleted);
	const creatingSuggestionIds = useAskPerformYardStore((state) => state.creatingSuggestionIds);
	const isCreatingAll = useAskPerformYardStore((state) => state.isCreatingAll);
	const { handleCreateGoal, handleCreateAllGoals } = useGoalCreation();

	// Lock body scroll when fullscreen
	React.useEffect(() => {
		if (!(isOpen && isExpanded)) return;
		const originalBodyOverflow = document.body.style.overflow;
		const originalHtmlOverflow = document.documentElement.style.overflow;
		document.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = originalBodyOverflow;
			document.documentElement.style.overflow = originalHtmlOverflow;
		};
	}, [isOpen, isExpanded]);

	if (!isOpen) {
		return null;
	}

	return createPortal(
		<div className={isExpanded ? "fixed inset-0 z-[1300] flex justify-end" : "fixed right-6 bottom-24 z-[1200]"}>
			{/* Backdrop — fullscreen only */}
			{isExpanded && (
				<button
					type="button"
					className="absolute inset-0 animate-in border-none bg-black/30 duration-200 fade-in"
					onClick={close}
					aria-label="Close chat"
				/>
			)}

			{/* Chat panel — key ensures React preserves identity when siblings change */}
			<div
				key="chat-panel"
				className={
					isExpanded
						? "relative flex h-full w-[416px] animate-in flex-col bg-white shadow-[0_4px_30px_0_rgba(0,0,0,0.15)] duration-200 slide-in-from-right-4"
						: "flex h-[776px] max-h-[calc(100vh-172px)] w-[416px] animate-in flex-col overflow-hidden rounded-lg bg-white shadow-[0_4px_30px_0_rgba(0,0,0,0.15)] duration-200 slide-in-from-bottom-4"
				}
				role="dialog"
				aria-label="Ask PerformYard Chat"
				aria-modal={isExpanded || undefined}
			>
				<ChatContent />
			</div>

			{/* Goal Edit Panel — fullscreen only */}
			{isExpanded && isGoalEditPanelOpen && (
				<div className="relative h-full w-[640px] animate-in border-l border-solid border-neutral-2 bg-white duration-200 slide-in-from-right-4">
					<GoalEditPanel
						onClose={closeGoalEditPanel}
						onCreateGoal={handleCreateGoal}
						onCreateAllGoals={handleCreateAllGoals}
						onDeleteGoal={markGoalDeleted}
						creatingSuggestionIds={creatingSuggestionIds}
						isCreatingAll={isCreatingAll}
					/>
				</div>
			)}

			{/* Competency Edit Panel — fullscreen only */}
			{isExpanded && isCompetencyEditPanelOpen && (
				<div className="relative h-full w-[640px] animate-in border-l border-solid border-neutral-2 bg-white duration-200 slide-in-from-right-4">
					<CompetencyEditPanel onClose={closeCompetencyEditPanel} />
				</div>
			)}

			{/* Arrow pointer — floating only */}
			{!isExpanded && (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="27"
					height="16"
					viewBox="0 0 27 16"
					fill="none"
					className="absolute right-[13px] -bottom-3"
					style={{ width: "26.667px", height: "16px" }}
				>
					<path
						d="M11.4502 15.2191C12.4914 16.2603 14.1824 16.2603 15.2237 15.2191L25.8862 4.55655C26.6526 3.79019 26.8775 2.64897 26.461 1.64936C26.0445 0.649746 25.0782 0 23.9953 0L2.67026 0.00833059C1.59568 0.00833059 0.621063 0.658077 0.204559 1.65769C-0.211945 2.6573 0.0212975 3.79852 0.779335 4.56488L11.4418 15.2274L11.4502 15.2191Z"
						fill="white"
					/>
				</svg>
			)}
		</div>,
		document.body
	);
};

export default ChatContainer;
